import React, { useState, useEffect, useRef } from 'react';
import { Button, Flex, Grid, Icon, Option, Select, Text } from '@pega/cosmos-react-core';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { appState } from '../../../../interfaces/common';
import { updateSoundNotificationsPayload } from '../../../../interfaces/chat';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { updateAudioNotifications } from '../ChatApis';
import { useNavigate } from 'react-router';
import * as sounds from '../../../../assets/sounds';

const audioTypeMap: { [k: string]: number } = {
  sound1: 1,
  sound2: 2,
  sound3: 3,
  sound4: 4,
  sound5: 5,
};

const SoundNotifications: React.FC<{}> = () => {
  const [playWhen, setPlayWhen] = useState('off');
  const [soundSelected, setSound] = useState('sound1');
  const { connectionId, audioPlayOn, audioType } = useSelector((state: appState) => state.chatInfo);
  const { sessionId } = useSelector((state: appState) => {
    return {
      sessionId: state.sessionId,
    };
  });
  const audioRef = useRef<HTMLAudioElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = useLocalTranslation();

  const updateNotificationSettings = async (audioType: number, audioPlayOn: string) => {
    const payload: updateSoundNotificationsPayload = { audioType, audioPlayOn };
    dispatch(updateLoaderDetails(true));
    const json = await updateAudioNotifications(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${translate('audio_notifications_updated')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('audio_notifications_update_failed'), 'urgent'));
    }
  };

  const updateChooseWhenToPlay = (whenToPlay: string) => {
    setPlayWhen(whenToPlay);
    updateNotificationSettings(audioTypeMap[soundSelected], whenToPlay);
  };
  const setAudioSource = (audioSelected: string) => {
    if (audioRef.current) {
      switch (audioSelected) {
        case 'sound1':
        case 'sound2':
        case 'sound3':
        case 'sound4':
        case 'sound5':
          audioRef.current.src = sounds[audioSelected];
          break;
        default:
          audioRef.current.src = sounds.sound1;
          break;
      }
    }
  };
  const setAudio = (audioSelected: string) => {
    setAudioSource(audioSelected);
    updateNotificationSettings(audioTypeMap[audioSelected], playWhen);
    setSound(audioSelected);
  };

  const playSelectedAudio = async () => {
    await audioRef?.current
      ?.play()
      .then(() => {})
      .catch(() => {});
  };

  useEffect(() => {
    if (audioPlayOn) setPlayWhen(audioPlayOn);
    if (audioType) {
      const sound = Object.keys(audioTypeMap).find((key) => audioTypeMap[key] === audioType) || 'sound1';
      setSound(sound);
      setAudioSource(sound);
    }
  }, [audioPlayOn, audioType]);
  return (
    <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
      <Text variant="h3" as="h3">
        {translate('audio_notifications')}
      </Text>
      <Grid
        container={{
          cols: 'repeat(3, 50% 40% 5%)',
          gap: 2,
          alignItems: 'end',
        }}
      >
        <Select
          label={translate('choose_when_to_play')}
          value={playWhen}
          required={true}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            updateChooseWhenToPlay(e.target.value);
          }}
        >
          <Option value="off">{translate('dont_play_sound')}</Option>
          <Option value="always">{translate('play_on_every_message')}</Option>
          <Option value="not_visible">{translate('play_when_not_visible')}</Option>
        </Select>
        {playWhen !== 'off' && (
          <>
            <Select
              label={translate('select_sound')}
              value={soundSelected}
              required={true}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setAudio(e.target.value);
              }}
            >
              {Object.keys(sounds).map((sound, index) => (
                <Option value={sound} key={sound}>{`${translate('sound')} ${index + 1}`}</Option>
              ))}
            </Select>
            <Button onClick={playSelectedAudio} icon variant="primary" style={{ height: 20, width: 20 }}>
              <Icon name={'play-solid'} style={{ cursor: 'pointer' }} />
            </Button>
          </>
        )}
        <audio ref={audioRef} style={{ display: 'none' }}>
          <source src={sounds.sound1}></source>
        </audio>
      </Grid>
    </Flex>
  );
};

export default SoundNotifications;
